import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  IFilterParams,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { Component, computed, DestroyRef, inject, OnInit, signal, ViewChild, ViewEncapsulation } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatSortModule } from "@angular/material/sort";
import { MatTableModule } from "@angular/material/table";
import { AccessLevel, AuthorizedAccess, Marketplace, UserApi } from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import { Marketplaces } from "@front/m19-models";
import { AccountMarketplaceService, AuthService, OrganizationUsersService } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonComponent,
  IInputComponent,
  MODAL_DATA,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { IRadioComponent } from "libs/m19-ui/src/components/iradio/iradio.component";
import { iif, Observable, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from "rxjs/operators";

export enum UserAccessMode {
  UPDATE_ACCESS,
  ADD_ACCESS,
}

export interface ManageAuthorizedAccessModalData {
  accessMode: UserAccessMode;
  userEmail?: string;
  userName?: string;
  board?: string;
  initialAuthorizedOrganizationProfileIds?: Map<number | undefined, AuthorizedAccess>;
  organizationId: number;
  organizationName: string;
  adminsEmails?: string[];
  authorizedUserEmails?: string[];
}

interface ManageAccessGridData {
  profileId: number;
  accountName: string;
  marketplace: Marketplace;
  accessLevel: AccessLevel | undefined;
  isUpdated: boolean;
}

export function accessEmailValidator(adminsEmails: string[], authorizedUserEmails: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (adminsEmails.includes(control.value)) {
      return { isAdmin: true };
    }
    if (authorizedUserEmails.includes(control.value)) {
      return { isAuthorizedUser: true };
    }
    return null;
  };
}

export const accessHeaderName: Record<AccessLevel.STATS_ONLY | AccessLevel.READ | AccessLevel.WRITE, string> = {
  [AccessLevel.STATS_ONLY]: "360 Dashboards Access",
  [AccessLevel.READ]: "Read Only Access",
  [AccessLevel.WRITE]: "Read Write Access",
};

@Component({
  selector: "app-manage-authorized-user-modal",
  templateUrl: "./manage-authorized-access-modal.component.html",
  styleUrls: ["./manage-authorized-access-modal.component.scss"],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    IButtonComponent,
    IInputComponent,
    IAlertComponent,
    AgGridAngular,
  ],
})
export class ManageAuthorizedAccessModalComponent implements OnInit {
  readonly UserAccessMode = UserAccessMode;
  private readonly authService = inject(AuthService);

  readonly modalData = inject(MODAL_DATA) as ManageAuthorizedAccessModalData;
  readonly modalRef = inject(ModalRef);
  readonly destroyRef = inject(DestroyRef);

  submmitButtonLabel = computed(() => {
    if (this.isEmailRegistered() === false) return this.translocoService.translate("user-management.send_invite");
    if (this.modalData.accessMode === UserAccessMode.ADD_ACCESS)
      return this.translocoService.translate("user-management.grant_access");
    return this.translocoService.translate("user-management.update_access");
  });

  manageAccessGridData = signal<ManageAccessGridData[]>([]);
  isAllNoAccess = computed(() => this.manageAccessGridData().every((x) => !x.accessLevel));
  isAllStatsOnlyAccess = computed(() =>
    this.manageAccessGridData().every((x) => x.accessLevel === AccessLevel.STATS_ONLY),
  );
  isAllReadOnlyAccess = computed(() => this.manageAccessGridData().every((x) => x.accessLevel === AccessLevel.READ));
  isAllReadWriteAccess = computed(() => this.manageAccessGridData().every((x) => x.accessLevel === AccessLevel.WRITE));

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular<ManageAccessGridData>;

  private accessColDefs = (): ColDef<ManageAccessGridData>[] => {
    return [AccessLevel.STATS_ONLY, AccessLevel.READ, AccessLevel.WRITE].map((accessLevel) => ({
      field: "accessLevel",
      cellClass: "flex items-center ",
      headerComponentSelector: (_: ICellRendererParams<ManageAccessGridData, any>) => {
        return {
          component: IRadioComponent,
          params: {
            checked:
              accessLevel === AccessLevel.STATS_ONLY
                ? this.isAllStatsOnlyAccess()
                : accessLevel === AccessLevel.READ
                  ? this.isAllReadOnlyAccess()
                  : this.isAllReadWriteAccess(),
            label: accessHeaderName[accessLevel as keyof typeof accessHeaderName],
            onClick: () => this.updateAccessLevelForAll(accessLevel),
          },
        };
      },
      valueGetter: (params: ValueGetterParams<ManageAccessGridData, any>) => {
        return params.data && params.data?.accessLevel === accessLevel;
      },
      cellRendererSelector: (params: ICellRendererParams<ManageAccessGridData, boolean>) => {
        return {
          component: IRadioComponent,
          params: {
            checked: params.value,
            onClick: () => {
              this.updateAccessLevel(params.data!.profileId, accessLevel);
            },
          },
        };
      },
    }));
  };

  private columnDefs: ColDef<ManageAccessGridData>[] = [
    { field: "accountName" },
    {
      field: "marketplace",
      headerName: "",
      valueFormatter: (params: ValueFormatterParams<ManageAccessGridData, any>) => {
        return Marketplaces[params.value as Marketplace].flag;
      },
    },
    {
      field: "accessLevel",
      cellClass: "flex items-center",
      headerComponent: IRadioComponent,
      headerComponentParams: (params: ICellRendererParams<ManageAccessGridData, any>) => {
        return {
          checked: this.isAllNoAccess(),
          label: this.translocoService.translate("manage-authorized-access-modal.no_access"),
          onClick: () => this.updateAccessLevelForAll(undefined),
        };
      },
      valueGetter: (params: ValueGetterParams<ManageAccessGridData, any>) => {
        return !params.data?.accessLevel;
      },
      cellRendererSelector: (params: ICellRendererParams<ManageAccessGridData, boolean>) => {
        return {
          component: IRadioComponent,
          params: {
            checked: params.value,
            onClick: () => {
              this.updateAccessLevel(params.data!.profileId, undefined);
            },
          },
        };
      },
    },
    ...this.accessColDefs(),
  ];

  gridOptions: GridOptions<ManageAccessGridData> = {
    ...gridConfig({}),
    columnDefs: this.columnDefs,
    defaultColDef: {
      flex: 1,
    },
    sideBar: false,
    pagination: true,
    paginationAutoPageSize: true,
  };

  private readonly loggedUser = toSignal(this.authService.loggedUser$);
  accountNameFilter = signal<string | null>(null);
  readonly emailForm = new FormGroup({
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
  });

  get email(): AbstractControl {
    return this.emailForm.controls.email;
  }

  grantOrUpdateAccessLoading = signal(false);
  checkEmailLoading = signal(false);

  private readonly emailValue$: Observable<string | null> = this.email.valueChanges.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    filter((_) => this.emailForm.controls.email.valid && this.email.value !== null),
  );

  emailValue = toSignal<string | null>(this.emailValue$);

  isEmailRegistered = toSignal(
    this.emailValue$.pipe(
      tap(() => this.grantOrUpdateAccessLoading.set(true)),
      switchMap((email) => {
        if (!email) return of(true);
        return this.userApi.doesUserExist({ email }).pipe(
          map((_) => true),
          catchError((_) => of(false)),
        );
      }),
      tap(() => this.grantOrUpdateAccessLoading.set(false)),
    ),
  );

  private updateAccessLevel(profileId: number, accessLevel: AccessLevel | undefined): void {
    this.manageAccessGridData.update((data) => {
      const index = data.findIndex((x) => x.profileId === profileId);
      if (index !== -1) {
        data[index].accessLevel = accessLevel;
        data[index].isUpdated = true;
      }
      return [...data];
    });
    this.agGrid.api?.refreshHeader();
  }

  private updateAccessLevelForAll(accessLevel: AccessLevel | undefined): void {
    this.agGrid?.api?.forEachNodeAfterFilter((node) => {
      this.updateAccessLevel(node.data!.profileId, accessLevel);
    });
    this.agGrid.api?.refreshHeader();
  }

  constructor(
    private accountMarketplaceService: AccountMarketplaceService,
    private toasterService: ToasterService,
    private organizationUsersService: OrganizationUsersService,
    private userApi: UserApi,
    private translocoService: TranslocoService,
  ) {
    if (this.modalData.accessMode === UserAccessMode.ADD_ACCESS) {
      this.email.addValidators(
        accessEmailValidator(this.modalData.adminsEmails ?? [], this.modalData.authorizedUserEmails ?? []),
      );
      this.email.updateValueAndValidity();
    } else {
      this.email.setValue(this.modalData.userEmail);
    }
  }

  isExternalFilterPresent = (): boolean => {
    return !!this.accountNameFilter();
  };

  doesExternalFilterPass = (node: IRowNode<ManageAccessGridData>): boolean => {
    return node.data?.accountName.toLowerCase().includes(this.accountNameFilter()?.toLowerCase() ?? "") ?? false;
  };

  externalFilterChanged(newValue: string): void {
    this.accountNameFilter.set(newValue);
    this.agGrid.api?.onFilterChanged();
  }

  ngOnInit(): void {
    this.accountMarketplaceService.accountMarketplaces$
      .pipe(
        map((accountMarketplaces) => {
          return accountMarketplaces?.filter((x) => x.resourceOrganizationId === this.modalData.organizationId);
        }),
      )
      .subscribe((accountMarketplaces) => {
        const data: ManageAccessGridData[] = [];
        accountMarketplaces?.forEach((x) => {
          data.push({
            profileId: x.profileId!,
            accountName: x.accountName!,
            marketplace: x.marketplace!,
            accessLevel: this.modalData.initialAuthorizedOrganizationProfileIds?.get(x.profileId!)?.accessLevel,
            isUpdated: false,
          });
        });

        this.manageAccessGridData.set(data);
      });
  }

  save(): void {
    if (
      this.modalData.accessMode === UserAccessMode.ADD_ACCESS &&
      this.modalData.adminsEmails?.includes(this.email.value)
    ) {
      this.toasterService.error("The provided user is already an Admin", "Admin Setting");
      return;
    }
    if (
      this.modalData.accessMode === UserAccessMode.ADD_ACCESS &&
      this.modalData.authorizedUserEmails?.includes(this.email.value)
    ) {
      this.toasterService.error("The provided user is already an Authorized User", "Admin Setting");
      return;
    }
    if (this.manageAccessGridData().some((x) => x.isUpdated && x.accessLevel)) this.handleAddAccess();
    if (this.manageAccessGridData().some((x) => x.isUpdated && !x.accessLevel)) this.revokeAuthorizedAccess();
  }

  private handleAddAccess() {
    if (this.email.invalid) return;

    this.grantOrUpdateAccessLoading.set(true);
    const initCall = iif(
      () => !this.isEmailRegistered() && this.modalData.accessMode === UserAccessMode.ADD_ACCESS,
      this.userApi.registerUser({
        userBase: {
          email: this.email.value,
          invitedBy: this.loggedUser()?.userId,
        },
      }),
      of({}),
    );

    initCall
      .pipe(
        switchMap((_) => this.addAuthorizedAccess()),
        switchMap((_) =>
          iif(
            () => !this.isEmailRegistered() && this.modalData.accessMode === UserAccessMode.ADD_ACCESS,
            this.userApi
              .sendUserInvitation({
                email: this.email.value,
                domain: window.location.hostname,
                organizationId: this.modalData.organizationId,
              })
              .pipe(catchAjaxError()),
            of({}),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          const message =
            this.modalData.accessMode === UserAccessMode.ADD_ACCESS
              ? this.translocoService.translate("user-management.success_invite_sent", [this.email.value])
              : this.translocoService.translate("user-management.grant_access", [this.email.value]);
          this.toasterService.success(message);
          this.grantOrUpdateAccessLoading.set(false);
          this.modalRef.close();
        },
        error: (error) => {
          this.toasterService.error(error);
          this.grantOrUpdateAccessLoading.set(false);
        },
      });
  }

  private addAuthorizedAccess(): Observable<void> {
    const authorizedAccessUpdates: AuthorizedAccess[] = this.manageAccessGridData()
      .filter((x) => x.isUpdated && x.accessLevel)
      .map((x) => ({
        organizationId: this.modalData.organizationId,
        profileId: x.profileId,
        email: this.email.value,
        accessLevel: x.accessLevel,
      }));
    return this.organizationUsersService.addAuthorizedAccessAsync(authorizedAccessUpdates);
  }

  private revokeAuthorizedAccess(): void {
    this.grantOrUpdateAccessLoading.set(true);
    const revokedAccesses: AuthorizedAccess[] = this.manageAccessGridData()
      .filter((x) => x.isUpdated && !x.accessLevel)
      .map((x) => ({
        organizationId: this.modalData.organizationId,
        profileId: x.profileId,
        email: this.email.value,
      }));

    this.organizationUsersService.revokeAuthorizedAccessAsync(revokedAccesses).subscribe({
      next: () => {
        this.toasterService.success(
          "Successfully revoked access from " + this.email.value + " to " + revokedAccesses.length + " accounts",
          "Access revoked",
        );
        this.grantOrUpdateAccessLoading.set(false);
        this.modalRef.close();
      },
      error: (error) => {
        this.toasterService.error(error, "Access revocation error");
        this.grantOrUpdateAccessLoading.set(false);
      },
    });
  }
}
